import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { EntityImage, EntityImageGallery } from '../components/EntityImage';
import { ArrowLeft, Wrench, X, Upload, Trash2, Edit, Calendar, DollarSign, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { MAX_IMAGES_PER_ITEM, canAddMoreImages, getImageCountDisplay, validateImageUpload } from '../lib/imageLimit';

const ACCESSORY_TYPES = ['Optic', 'Magazine', 'Holster', 'Light', 'Laser', 'Sling', 'Suppressor', 'Grip', 'Stock', 'Rail', 'Bipod', 'Case', 'Cleaning Kit', 'Other'];

export default function AccessoryFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const isDetail = isEdit && !window.location.pathname.includes('/edit');

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [accessory, setAccessory] = useState(null);
  const [firearms, setFirearms] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    brand: '',
    model: '',
    notes: '',
    purchase_date: '',
    purchase_price: '',
    compatible_firearms: [],
    images: [],
  });

  useEffect(() => {
    // Fetch firearms for linking
    api.get('/firearms').then(res => setFirearms(res.data)).catch(() => {});

    if (isEdit) {
      const fetchAccessory = async () => {
        try {
          const response = await api.get(`/accessories/${id}`);
          setAccessory(response.data);
          setFormData({
            name: response.data.name || '',
            type: response.data.type || '',
            brand: response.data.brand || '',
            model: response.data.model || '',
            notes: response.data.notes || '',
            purchase_date: response.data.purchase_date || '',
            purchase_price: response.data.purchase_price?.toString() || '',
            compatible_firearms: response.data.compatible_firearms || [],
            images: response.data.images || [],
          });
        } catch (error) {
          toast.error('Accessory not found');
          navigate('/accessories');
        } finally {
          setLoading(false);
        }
      };
      fetchAccessory();
    }
  }, [id, isEdit, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const { acceptedFiles, message } = validateImageUpload(files, formData.images.length);
    
    if (message) {
      toast.error(message);
    }
    
    if (acceptedFiles.length === 0) {
      e.target.value = '';
      return;
    }
    
    acceptedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, reader.result],
        }));
      };
      reader.readAsDataURL(file);
    });
    
    e.target.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const toggleFirearm = (firearmId) => {
    setFormData(prev => ({
      ...prev,
      compatible_firearms: prev.compatible_firearms.includes(firearmId)
        ? prev.compatible_firearms.filter(id => id !== firearmId)
        : [...prev.compatible_firearms, firearmId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.type) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
      };

      if (isEdit) {
        await api.put(`/accessories/${id}`, payload);
        toast.success('Accessory updated');
      } else {
        await api.post('/accessories', payload);
        toast.success('Accessory added');
      }
      navigate('/accessories');
    } catch (error) {
      console.error('Error saving accessory:', error);
      toast.error('Failed to save accessory');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/accessories/${id}`);
      toast.success('Accessory deleted');
      navigate('/accessories');
    } catch (error) {
      console.error('Error deleting accessory:', error);
      toast.error('Failed to delete accessory');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout title={isEdit ? 'Accessory Details' : 'Add Accessory'}>
        <div className="animate-pulse space-y-6 max-w-2xl">
          <div className="h-10 bg-muted rounded w-full" />
          <div className="h-10 bg-muted rounded w-full" />
        </div>
      </Layout>
    );
  }

  // Detail view
  if (isDetail && accessory) {
    const linkedFirearms = firearms.filter(f => accessory.compatible_firearms?.includes(f.id));

    return (
      <Layout title="Accessory Details">
        <div className="space-y-6" data-testid="accessory-detail-page">
          <Button
            variant="ghost"
            onClick={() => navigate('/accessories')}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Accessories
          </Button>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Image Gallery - Using EntityImageGallery for object URL rendering */}
            <EntityImageGallery
              imageFileIds={accessory.images}
              alt={accessory.name}
              className="aspect-square bg-card border border-border rounded-sm overflow-hidden bg-muted"
              thumbnailClassName="w-full h-full object-cover"
              fallback={
                <div className="w-full h-full flex items-center justify-center bg-muted">
                  <Wrench className="w-24 h-24 text-muted-foreground/30" />
                </div>
              }
            />

            {/* Details */}
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground font-['Chivo'] tracking-tight">
                    {accessory.name}
                  </h1>
                  <p className="text-muted-foreground">
                    {accessory.type}
                    {accessory.brand && ` • ${accessory.brand}`}
                    {accessory.model && ` ${accessory.model}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/accessories/${id}/edit`)}
                    className="rounded-sm"
                    data-testid="edit-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        data-testid="delete-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Accessory</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this accessory?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDelete}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          disabled={deleting}
                        >
                          {deleting ? 'Deleting...' : 'Delete'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {/* Purchase Info */}
              {(accessory.purchase_date || accessory.purchase_price) && (
                <div className="bg-card border border-border rounded-sm p-6 space-y-4">
                  <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                    Purchase Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {accessory.purchase_date && (
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">Date</p>
                        <p className="font-medium flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {accessory.purchase_date}
                        </p>
                      </div>
                    )}
                    {accessory.purchase_price && (
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">Price</p>
                        <p className="font-medium flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-muted-foreground" />
                          ${accessory.purchase_price.toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Compatible Firearms */}
              {linkedFirearms.length > 0 && (
                <div className="bg-card border border-border rounded-sm p-6">
                  <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                    Compatible Firearms
                  </h3>
                  <div className="space-y-2">
                    {linkedFirearms.map(f => (
                      <div
                        key={f.id}
                        className="flex items-center gap-3 p-2 hover:bg-muted rounded-sm cursor-pointer transition-colors"
                        onClick={() => navigate(`/firearms/${f.id}`)}
                      >
                        <div className="w-10 h-10 bg-muted rounded-sm flex items-center justify-center overflow-hidden">
                          {f.images?.[0] ? (
                            <EntityImage fileId={f.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Wrench className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{f.manufacturer} {f.model}</p>
                          <p className="text-xs text-muted-foreground">{f.caliber}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {accessory.notes && (
                <div className="bg-card border border-border rounded-sm p-6">
                  <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                    Notes
                  </h3>
                  <p className="text-foreground whitespace-pre-wrap">{accessory.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Form view
  return (
    <Layout title={isEdit ? 'Edit Accessory' : 'Add Accessory'}>
      <div className="max-w-2xl" data-testid="accessory-form-page">
        <Button
          variant="ghost"
          onClick={() => navigate('/accessories')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Accessories
        </Button>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
              Basic Information
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Aimpoint PRO"
                  className="rounded-sm"
                  required
                  data-testid="input-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Type *</Label>
                <Select value={formData.type} onValueChange={(v) => handleSelectChange('type', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="select-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCESSORY_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="brand">Brand</Label>
                <Input
                  id="brand"
                  name="brand"
                  value={formData.brand}
                  onChange={handleInputChange}
                  placeholder="e.g., Aimpoint, Surefire"
                  className="rounded-sm"
                  data-testid="input-brand"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  name="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  placeholder="e.g., PRO, X300"
                  className="rounded-sm"
                  data-testid="input-model"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="purchase_date">Purchase Date</Label>
                <Input
                  id="purchase_date"
                  name="purchase_date"
                  type="date"
                  value={formData.purchase_date}
                  onChange={handleInputChange}
                  className="rounded-sm"
                  data-testid="input-purchase-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="purchase_price">Purchase Price ($)</Label>
                <Input
                  id="purchase_price"
                  name="purchase_price"
                  type="number"
                  step="0.01"
                  value={formData.purchase_price}
                  onChange={handleInputChange}
                  placeholder="e.g., 449.99"
                  className="rounded-sm"
                  data-testid="input-purchase-price"
                />
              </div>
            </div>
          </div>

          {/* Compatible Firearms */}
          {firearms.length > 0 && (
            <div className="bg-card border border-border rounded-sm p-6 space-y-4">
              <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                Compatible Firearms
              </h3>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {firearms.map(f => (
                  <label
                    key={f.id}
                    className={`flex items-center gap-2 p-2 rounded-sm cursor-pointer transition-colors ${
                      formData.compatible_firearms.includes(f.id) ? 'bg-secondary/10' : 'hover:bg-muted'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.compatible_firearms.includes(f.id)}
                      onChange={() => toggleFirearm(f.id)}
                      className="rounded-sm"
                    />
                    <span className="text-sm">{f.manufacturer} {f.model}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Images */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo']">
                Images
              </h3>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                {getImageCountDisplay(formData.images.length)}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-4">
              {formData.images.map((img, idx) => (
                <div key={idx} className="relative aspect-square bg-muted rounded-sm overflow-hidden group">
                  <EntityImage fileId={img} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {canAddMoreImages(formData.images.length) && (
                <label className="aspect-square border-2 border-dashed border-border rounded-sm flex flex-col items-center justify-center cursor-pointer hover:border-secondary transition-colors">
                  <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                  <span className="text-xs text-muted-foreground">Upload</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="input-images"
                  />
                </label>
              )}
            </div>
            {formData.images.length >= MAX_IMAGES_PER_ITEM && (
              <p className="text-xs text-muted-foreground">
                Maximum {MAX_IMAGES_PER_ITEM} photos reached. Remove existing photos to add new ones.
              </p>
            )}
          </div>

          {/* Notes */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
              Notes
            </h3>
            <Textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Add any notes..."
              rows={3}
              className="rounded-sm"
              data-testid="input-notes"
            />
          </div>

          <div className="flex gap-4">
            <Button
              type="submit"
              disabled={saving}
              className="bg-secondary hover:bg-secondary/90 rounded-sm btn-tactical"
              data-testid="submit-btn"
            >
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Add Accessory'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/accessories')}
              className="rounded-sm"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
